import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/common/DataTable";
import { knowledgeService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";
import {
  ArrowLeft, Loader2, Save, Send, CheckCircle2, XCircle, Archive, RotateCcw, History,
  ShieldCheck, Eye, ThumbsUp, Boxes, FileText,
} from "lucide-react";
import { STATUS_COLOR, VISIBILITY_COLOR, VISIBILITIES, RESTRICTABLE_ROLES, VISIBILITY_HINT } from "./constants";

function Section({ title, icon: Icon, children, testid, actions }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />} {title}
        </h3>
        {actions}
      </div>
      {children}
    </div>
  );
}

export default function ArticleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tab, setTab] = useState("edit");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [versions, setVersions] = useState([]);
  const [chunks, setChunks] = useState(null);

  const canEdit = hasPermission("KNOWLEDGE.EDIT");
  const canApprove = hasPermission("KNOWLEDGE.APPROVE");
  const canPublish = hasPermission("KNOWLEDGE.PUBLISH");

  const load = useCallback(async () => {
    try {
      const data = await knowledgeService.getArticle(id);
      setA(data); setDirty(false);
      const [c, t] = await Promise.all([
        knowledgeService.listCategories({ tenantId: data.tenantId }),
        knowledgeService.listTags({ tenantId: data.tenantId }),
      ]);
      setCategories(c.items || []); setAllTags(t.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setField = (patch) => { setA((p) => ({ ...p, ...patch })); setDirty(true); };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveChanges = () => act(() => knowledgeService.updateArticle(id, {
    title: a.title, summary: a.summary, body: a.body, categoryId: a.categoryId || null,
    tags: a.tags || [], visibility: a.visibility, allowedRoles: a.allowedRoles || [],
  }), "Saved");

  const toggleTag = (name) => {
    const cur = a.tags || [];
    setField({ tags: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] });
  };
  const toggleRole = (rc) => {
    const cur = a.allowedRoles || [];
    setField({ allowedRoles: cur.includes(rc) ? cur.filter((x) => x !== rc) : [...cur, rc] });
  };

  const loadVersions = () => knowledgeService.versions(id).then((d) => setVersions(d.versions || [])).catch(() => {});
  const loadChunks = () => knowledgeService.chunks(id).then(setChunks).catch(() => {});

  useEffect(() => { if (tab === "versions") loadVersions(); if (tab === "chunks") loadChunks(); /* eslint-disable-next-line */ }, [tab]);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!a) return null;

  const editable = canEdit && ["Draft", "Rejected"].includes(a.status);

  return (
    <div data-testid="article-detail-page">
      <button onClick={() => navigate("/admin/knowledge/articles")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="article-back">
        <ArrowLeft className="h-4 w-4" /> Back to articles
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge color={STATUS_COLOR[a.status]}>{a.status}</Badge>
            <Badge color={VISIBILITY_COLOR[a.visibility]}>{a.visibility}</Badge>
            <span className="text-xs text-muted-foreground">v{a.currentVersion}</span>
            {a.approval?.rejectedReason && a.status === "Rejected" && <span className="text-xs text-red-600">{a.approval.rejectedReason}</span>}
          </div>
          <h1 className="text-2xl font-bold text-foreground truncate">{a.title}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="article-actions">
          {editable && (
            <button onClick={saveChanges} disabled={busy || !dirty} className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50" data-testid="article-save-btn">
              <Save className="h-4 w-4" /> Save
            </button>
          )}
          {editable && (
            <button onClick={() => act(() => knowledgeService.articleAction(id, "submit"), "Submitted for review")} disabled={busy || dirty} title={dirty ? "Save your changes first" : ""}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="article-submit-btn">
              <Send className="h-4 w-4" /> Submit for review
            </button>
          )}
          {a.status === "In Review" && canApprove && (
            <>
              <button onClick={() => act(() => knowledgeService.articleAction(id, "approve"), "Approved & published")} disabled={busy}
                className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60" data-testid="article-approve-btn">
                <CheckCircle2 className="h-4 w-4" /> Approve &amp; Publish
              </button>
              <button onClick={() => { const r = window.prompt("Rejection reason?"); if (r !== null) act(() => knowledgeService.articleAction(id, "reject", { reason: r }), "Rejected"); }} disabled={busy}
                className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-60" data-testid="article-reject-btn">
                <XCircle className="h-4 w-4" /> Reject
              </button>
            </>
          )}
          {a.status === "Published" && canPublish && (
            <button onClick={() => act(() => knowledgeService.articleAction(id, "archive"), "Archived")} disabled={busy}
              className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-2 text-sm hover:bg-muted disabled:opacity-60" data-testid="article-archive-btn">
              <Archive className="h-4 w-4" /> Archive
            </button>
          )}
          {a.status === "Archived" && canPublish && (
            <button onClick={() => act(() => knowledgeService.articleAction(id, "republish"), "Republished")} disabled={busy}
              className="inline-flex items-center gap-1.5 bg-teal-600 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-teal-700 disabled:opacity-60" data-testid="article-republish-btn">
              <RotateCcw className="h-4 w-4" /> Republish
            </button>
          )}
        </div>
      </div>

      <div className="flex border-b border-border mb-6">
        {[["edit", "Content"], ["meta", "Visibility & Tags"], ["versions", "Versions"], ["chunks", "RAG Chunks"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`article-tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === "edit" && (
        <div className="space-y-4">
          {editable && (
            <input value={a.title} onChange={(e) => setField({ title: e.target.value })} className="ciq-input text-lg font-semibold" data-testid="article-edit-title" />
          )}
          {editable && (
            <textarea value={a.summary || ""} onChange={(e) => setField({ summary: e.target.value })} className="ciq-input" rows={2} placeholder="Short summary" data-testid="article-edit-summary" />
          )}
          {!editable && a.summary && <p className="text-sm text-muted-foreground">{a.summary}</p>}
          <RichTextEditor value={a.body} onChange={(html) => setField({ body: html })} editable={editable} />
          {!editable && <p className="text-xs text-muted-foreground">This article is <strong>{a.status}</strong> and read-only. {a.status === "Published" ? "Editing creates a new draft version." : ""}</p>}
        </div>
      )}

      {tab === "meta" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="Visibility" icon={ShieldCheck} testid="article-visibility-section">
            <select value={a.visibility} disabled={!editable} onChange={(e) => setField({ visibility: e.target.value })} className="ciq-input" data-testid="article-visibility-select">
              {VISIBILITIES.map((v) => <option key={v}>{v}</option>)}
            </select>
            <p className="text-xs text-muted-foreground mt-2">{VISIBILITY_HINT[a.visibility]}</p>
            {a.visibility === "Restricted" && (
              <div className="mt-3">
                <p className="text-xs font-medium text-foreground mb-1.5">Allowed roles</p>
                <div className="flex flex-wrap gap-1.5" data-testid="article-allowed-roles">
                  {RESTRICTABLE_ROLES.map((rc) => {
                    const on = (a.allowedRoles || []).includes(rc);
                    return (
                      <button key={rc} disabled={!editable} onClick={() => toggleRole(rc)}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"} disabled:opacity-60`}
                        data-testid={`article-role-${rc}`}>{rc}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </Section>
          <Section title="Category & Tags" icon={FileText} testid="article-tags-section">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
            <select value={a.categoryId || ""} disabled={!editable} onChange={(e) => setField({ categoryId: e.target.value || null })} className="ciq-input mb-3" data-testid="article-category-select">
              <option value="">None</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Tags</label>
            <div className="flex flex-wrap gap-1.5">
              {allTags.length === 0 && <p className="text-xs text-muted-foreground">No tags yet — create them in the Tags page.</p>}
              {allTags.map((t) => {
                const on = (a.tags || []).includes(t.name);
                return (
                  <button key={t.id} disabled={!editable} onClick={() => toggleTag(t.name)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"} disabled:opacity-60`}
                    data-testid={`article-tag-${t.name}`}>{t.name}</button>
                );
              })}
            </div>
          </Section>
          <Section title="Engagement" icon={Eye} testid="article-engagement-section">
            <div className="flex items-center gap-6 text-sm">
              <span className="flex items-center gap-1.5 text-foreground"><Eye className="h-4 w-4 text-muted-foreground" /> {a.viewCount ?? 0} views</span>
              <span className="flex items-center gap-1.5 text-foreground"><ThumbsUp className="h-4 w-4 text-muted-foreground" /> {a.helpfulCount ?? 0} helpful</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Engagement counters are a placeholder for future knowledge analytics & AI ranking.</p>
          </Section>
          <Section title="Approval" icon={ShieldCheck} testid="article-approval-section">
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge color={STATUS_COLOR[a.status]}>{a.status}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Submitted</span><span className="text-foreground">{a.approval?.submittedAt ? a.approval.submittedAt.slice(0, 16).replace("T", " ") : "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Approved</span><span className="text-foreground">{a.approval?.approvedAt ? a.approval.approvedAt.slice(0, 16).replace("T", " ") : "—"}</span></div>
            </div>
          </Section>
        </div>
      )}

      {tab === "versions" && (
        <Section title="Version History" icon={History} testid="article-versions-section">
          {versions.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No previous versions yet — edits will snapshot here.</p> : (
            <div className="space-y-1.5">
              {[...versions].reverse().map((v) => (
                <div key={v.version} className="flex items-center justify-between text-sm border-b border-border py-2" data-testid={`article-version-${v.version}`}>
                  <span className="text-foreground font-medium">v{v.version} — {v.title}</span>
                  <span className="text-xs text-muted-foreground">{(v.savedAt || "").slice(0, 16).replace("T", " ")}</span>
                  {canEdit && <button onClick={() => act(() => knowledgeService.restoreVersion(id, v.version), `Restored v${v.version}`)} disabled={busy}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`article-restore-${v.version}`}><RotateCcw className="h-3 w-3" /> Restore</button>}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {tab === "chunks" && (
        <Section title="RAG-ready Chunks" icon={Boxes} testid="article-chunks-section">
          {!chunks ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : (
            <>
              <p className="text-xs text-muted-foreground mb-3">{chunks.count} chunk(s) · embedding status: <strong className="text-foreground">{chunks.embeddingStatus}</strong>. Generated on publish; vectors fill in a future RAG phase.</p>
              {chunks.count === 0 ? <p className="text-sm text-muted-foreground">No chunks yet — publish the article to generate them.</p> : (
                <div className="space-y-2">
                  {chunks.chunks.map((c) => (
                    <div key={c.id} className="rounded-lg border border-border p-3 text-xs" data-testid={`article-chunk-${c.chunkIndex}`}>
                      <div className="flex items-center justify-between mb-1 text-muted-foreground"><span>chunk #{c.chunkIndex}</span><span>~{c.tokensEstimate} tokens · {c.status}</span></div>
                      <p className="text-foreground line-clamp-3">{c.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Section>
      )}
    </div>
  );
}
